import { strings } from '@thoughtspot/translations';
import _ from 'lodash';
import { NameValuePairs } from '../font-metric-service';
export var FontFaceFormat;
(function (FontFaceFormat) {
    FontFaceFormat[FontFaceFormat["WOFF"] = 0] = "WOFF";
    FontFaceFormat[FontFaceFormat["WOFF2"] = 1] = "WOFF2";
})(FontFaceFormat || (FontFaceFormat = {}));
export var FontFaceStyle;
(function (FontFaceStyle) {
    FontFaceStyle[FontFaceStyle["NORMAL"] = 0] = "NORMAL";
    FontFaceStyle[FontFaceStyle["ITALIC"] = 1] = "ITALIC";
    FontFaceStyle[FontFaceStyle["OBLIQUE"] = 2] = "OBLIQUE";
})(FontFaceStyle || (FontFaceStyle = {}));
export var FontFaceWeight;
(function (FontFaceWeight) {
    FontFaceWeight[FontFaceWeight["NORMAL"] = 400] = "NORMAL";
    FontFaceWeight[FontFaceWeight["LIGHT"] = 200] = "LIGHT";
    FontFaceWeight[FontFaceWeight["BOLD"] = 700] = "BOLD";
})(FontFaceWeight || (FontFaceWeight = {}));
export class FontFace {
    constructor(guid, family, format, url = null, weight = null, style = null, size = NaN, unicodeRange = null, variant = null, stretch = null, color = null) {
        this.guid = guid;
        this.family = family;
        this.format = format;
        this.url = url;
        this.weight = weight;
        this.style = style;
        this.size = size;
        this.unicodeRange = unicodeRange;
        this.variant = variant;
        this.stretch = stretch;
        this.color = color;
    }
    static fromJson(json) {
        return new FontFace(json.guid, json.family, json.format, json.url, json.weight, json.style, json.size, json.unicodeRange, json.variant, json.strectch, json.color);
    }
    static convertFontFaceStyleToString(fontFaceStyle) {
        switch (fontFaceStyle) {
            case FontFaceStyle.ITALIC:
                return 'italic';
            case FontFaceStyle.OBLIQUE:
                return 'oblique';
            default:
                return 'normal';
        }
    }
    static convertFontFormatToString(fontFormat) {
        switch (fontFormat) {
            case FontFaceFormat.WOFF2:
                return 'WOFF2';
            default:
                return 'WOFF';
        }
    }
    static getQuotedFontFamilyName(fontFamily) {
        const familyNames = fontFamily.split(',');
        const quotedFamilyNames = familyNames.map(function (familyName) {
            familyName = familyName.trim();
            if (fontFamily.slice(0, 3) === 'var') {
                return `${familyName}`;
            }
            return `'${familyName}'`;
        });
        return quotedFamilyNames.join(',');
    }
    toJson() {
        return {
            guid: this.guid,
            family: this.family,
            format: this.format,
            url: this.url,
            weight: this.weight,
            style: this.style,
            size: this.size,
            unicodeRange: this.unicodeRange,
            variant: this.variant,
            stretch: this.stretch,
            color: this.color,
        };
    }
    toStyle() {
        return this.createStyle();
    }
    toHtml() {
        return this.createHtml();
    }
    toFontFaceRule() {
        const fontFaceRule = {
            fontFamily: FontFace.getQuotedFontFamilyName(this.family),
            fontUrl: this.url,
            fontFormat: FontFace.convertFontFormatToString(this.format),
        };
        fontFaceRule.fontStyle =
            this.style !== null
                ? FontFace.convertFontFaceStyleToString(this.style)
                : FontFace.convertFontFaceStyleToString(FontFace.FontFaceDefaultValues.STYLE);
        fontFaceRule.fontWeight = _.isNumber(this.weight)
            ? this.weight
            : FontFace.FontFaceDefaultValues.WEIGHT;
        fontFaceRule.unicodeRange =
            this.unicodeRange !== null
                ? this.unicodeRange
                : FontFace.FontFaceDefaultValues.UNICODE_RANGE;
        fontFaceRule.fontVariant =
            this.variant !== null
                ? this.variant
                : FontFace.FontFaceDefaultValues.VARIANT;
        fontFaceRule.fontStretch =
            this.stretch !== null
                ? this.stretch
                : FontFace.FontFaceDefaultValues.STRETCH;
        return fontFaceRule;
    }
    isLocal() {
        return !this.guid;
    }
    createStyle() {
        const style = {
            fontFamily: FontFace.getQuotedFontFamilyName(this.family),
        };
        if (_.isNumber(this.weight)) {
            style.fontWeight = this.weight;
        }
        else {
            style.fontWeight = 'inherit';
        }
        if (this.style !== null) {
            style.fontStyle = FontFace.convertFontFaceStyleToString(this.style);
        }
        else {
            style.fontStyle = 'inherit';
        }
        if (_.isNumber(this.size)) {
            style.fontSize = `${this.size}px`;
        }
        else {
            style.fontSize = 'inherit';
        }
        if (this.color !== null) {
            style.color = this.color;
        }
        else {
            style.color = 'inherit';
        }
        style.textShadow = 'none';
        style.textOutline = false;
        return style;
    }
    createHtml() {
        const propertyKeysRoot = strings.fontPreview.fontProperties;
        const tooltipData = new NameValuePairs();
        tooltipData.add(propertyKeysRoot.FAMILY, this.family);
        if (_.isNumber(this.weight)) {
            tooltipData.add(propertyKeysRoot.WEIGHT, FontFaceWeight[this.weight]);
        }
        if (this.style) {
            tooltipData.add(propertyKeysRoot.STYLE, FontFaceStyle[this.style]);
        }
        if (_.isNumber(this.size)) {
            tooltipData.add(propertyKeysRoot.SIZE, this.size.toString());
        }
        if (this.unicodeRange) {
            tooltipData.add(propertyKeysRoot.UNICODE_RANGE, this.unicodeRange);
        }
        if (this.variant) {
            tooltipData.add(propertyKeysRoot.VARIANT, this.variant);
        }
        if (this.stretch) {
            tooltipData.add(propertyKeysRoot.VARIANT, this.stretch);
        }
        if (this.color) {
            tooltipData.add(propertyKeysRoot.COLOR, this.color);
        }
        return tooltipData.getTemplate();
    }
}
FontFace.FontFaceDefaultValues = {
    VARIANT: 'normal',
    STRETCH: 'normal',
    WEIGHT: FontFaceWeight.NORMAL,
    STYLE: FontFaceStyle.NORMAL,
    UNICODE_RANGE: 'U+0-10FFFF',
};
//# sourceMappingURL=font-face.js.map